/**
 * The session record on the wire: the body the backend stores, and back.
 *
 * One body is self-contained: the session header, materials, the takes (open or ended) and
 * sealed segments. Every part is idempotent on the server (a take or material by its id, a
 * segment by its `client_event_id`), so a body can be resent whole. Field names follow the
 * backend's columns; the engine's own data (scores, setups, events) travels as the engine
 * shapes it, and the server stores it whole.
 */

import {
  AssistLevels,
  Cell,
  Direction,
  FrontierCoordinates,
  GradedDimension,
  HandRole,
  InputMode,
  PresetId,
  RhythmDensity,
  Scale,
  TakeStamp,
  Tonic,
} from './stamp';
import { Surface } from '../presetData';
import {
  Material,
  RecordedTake,
  RecordedTakeSetup,
  Segment,
  SessionHeader,
  SessionRecord,
  StreamEvent,
  TakeEndReason,
} from './stream';
import { PositionScore, TakeCounts, TakeResult } from './takeResult';
import { GradingConfigError } from '../types';

/** The surface's own play-session row: one field per surface that has one. */
export interface WireSessionLinks {
  readonly play_session_id: string | null;
  readonly rep_play_session_id: string | null;
  readonly exercise_play_session_id: string | null;
}

export interface WireSession extends WireSessionLinks {
  readonly id: string;
  readonly surface: SessionHeader['surface'];
  readonly started_at: string;
  readonly format_version: number;
  readonly app_version: string | null;
  readonly off_the_record: boolean;
}

export interface WireMaterial {
  readonly id: string;
  readonly phrase_id: number | null;
  readonly at_ms: number;
  readonly score: Material['score'];
}

/** The cell, one field per backend column; only the surface's own fields are sent. */
export interface WireCell {
  readonly exercise_id?: number;
  readonly scale?: Scale;
  readonly tonic?: Tonic;
  readonly repertoire_id?: number;
  readonly transpose_semitones?: number;
  readonly level_number?: number;
  readonly time_signature_numerator?: number;
  readonly time_signature_denominator?: number;
  readonly chapter?: number;
}

/** The frontier coordinates, one field per backend column. */
export interface WireFrontier {
  readonly tempo_bpm: number | null;
  readonly hand_left: HandRole;
  readonly hand_right: HandRole;
  readonly graded: readonly GradedDimension[];
  /** Each assist used, at the highest level it reached; an assist not listed was off. */
  readonly assists: AssistLevels;
  readonly tier_number: number | null;
  readonly range_octaves: number | null;
  readonly direction: Direction | null;
  readonly rhythm_density: RhythmDensity | null;
}

export interface WireCounts {
  readonly notes_graded: number;
  readonly notes_matched: number;
  readonly notes_missed: number;
  readonly notes_unreached: number;
  readonly notes_wrong_pitch: number;
  readonly extra_notes: number;
}

export interface WirePosition {
  readonly position: string;
  readonly beat: number;
  readonly score_combined: number | null;
  readonly score_left: number | null;
  readonly score_right: number | null;
  readonly notes: number;
}

export interface WireTakeEnd {
  readonly end_ms: number;
  readonly end_reason: TakeEndReason;
  readonly grade: number | null;
  readonly grade_left: number | null;
  readonly grade_right: number | null;
  readonly counts: WireCounts;
  readonly positions: readonly WirePosition[];
  readonly segment_count: number;
  readonly event_count: number;
}

export interface WireTake {
  readonly id: string;
  readonly take_index: number;
  readonly material_id: string;
  readonly start_ms: number;
  readonly first_seq: number;
  readonly setup: RecordedTakeSetup;
  /** The stamp as it stands (the final one once the take has ended). */
  readonly cell: WireCell;
  readonly frontier: WireFrontier;
  readonly preset: PresetId;
  readonly input_mode: InputMode;
  readonly input_latency_ms: number | null;
  readonly tempo_fastest_bpm: number | null;
  readonly tempo_goal_bpm: number | null;
  readonly off_the_record: boolean;
  readonly engine_version: number;
  /** Present once the take has ended. */
  readonly end: WireTakeEnd | null;
}

export interface WireSegment {
  readonly client_event_id: string;
  readonly seq: number;
  readonly take_id: string | null;
  readonly off_the_record: boolean;
  readonly first_ms: number;
  readonly last_ms: number;
  readonly event_count: number;
  readonly events: readonly StreamEvent[];
}

export interface WireBatch {
  readonly session: WireSession;
  readonly materials: readonly WireMaterial[];
  readonly takes: readonly WireTake[];
  readonly segments: readonly WireSegment[];
}

export function wireCell(cell: Cell): WireCell {
  switch (cell.surface) {
    case 'exercise':
      return { exercise_id: cell.exerciseId, scale: cell.scale, tonic: cell.tonic };
    case 'repertoire':
      return { repertoire_id: cell.repertoireId, transpose_semitones: cell.transposeSemitones };
    case 'sight-reading':
      return {
        level_number: cell.levelNumber,
        time_signature_numerator: cell.timeSignatureNumerator,
        time_signature_denominator: cell.timeSignatureDenominator,
      };
    case 'tutorial':
      return { level_number: cell.levelNumber, chapter: cell.chapter };
  }
}

/** The cell a take on a surface counts toward. Throws on a field the surface's cell lacks. */
export function cellFromWire(surface: Surface, cell: WireCell): Cell {
  const required = (value: number | string | undefined, name: string) => {
    if (value === undefined || value === null) {
      throw new GradingConfigError(`a ${surface} cell needs ${name}`);
    }
    return value;
  };
  switch (surface) {
    case 'exercise':
      return {
        surface,
        exerciseId: required(cell.exercise_id, 'exercise_id') as number,
        scale: required(cell.scale, 'scale') as Scale,
        tonic: required(cell.tonic, 'tonic') as Tonic,
      };
    case 'repertoire':
      return {
        surface,
        repertoireId: required(cell.repertoire_id, 'repertoire_id') as number,
        transposeSemitones: required(cell.transpose_semitones, 'transpose_semitones') as number,
      };
    case 'sight-reading':
      return {
        surface,
        levelNumber: required(cell.level_number, 'level_number') as number,
        timeSignatureNumerator: required(
          cell.time_signature_numerator,
          'time_signature_numerator',
        ) as number,
        timeSignatureDenominator: required(
          cell.time_signature_denominator,
          'time_signature_denominator',
        ) as number,
      };
    case 'tutorial':
      return {
        surface,
        levelNumber: required(cell.level_number, 'level_number') as number,
        chapter: required(cell.chapter, 'chapter') as number,
      };
  }
}

export function wireFrontier(frontier: FrontierCoordinates): WireFrontier {
  return {
    tempo_bpm: frontier.tempoBpm,
    hand_left: frontier.hands.left,
    hand_right: frontier.hands.right,
    graded: frontier.graded,
    assists: frontier.assists,
    tier_number: frontier.tierNumber,
    range_octaves: frontier.rangeOctaves,
    direction: frontier.direction,
    rhythm_density: frontier.rhythmDensity,
  };
}

export function frontierFromWire(frontier: WireFrontier): FrontierCoordinates {
  return {
    tempoBpm: frontier.tempo_bpm,
    hands: { left: frontier.hand_left, right: frontier.hand_right },
    graded: frontier.graded,
    assists: frontier.assists,
    tierNumber: frontier.tier_number,
    rangeOctaves: frontier.range_octaves,
    direction: frontier.direction,
    rhythmDensity: frontier.rhythm_density,
  };
}

function wireCounts(counts: TakeCounts): WireCounts {
  return {
    notes_graded: counts.graded,
    notes_matched: counts.matched,
    notes_missed: counts.missed,
    notes_unreached: counts.unreached,
    notes_wrong_pitch: counts.wrongPitch,
    extra_notes: counts.extras,
  };
}

function countsFromWire(counts: WireCounts): TakeCounts {
  return {
    graded: counts.notes_graded,
    matched: counts.notes_matched,
    missed: counts.notes_missed,
    unreached: counts.notes_unreached,
    wrongPitch: counts.notes_wrong_pitch,
    extras: counts.extra_notes,
  };
}

function wirePosition(scored: PositionScore): WirePosition {
  return {
    position: scored.position,
    beat: scored.beat,
    score_combined: scored.combined,
    score_left: scored.left,
    score_right: scored.right,
    notes: scored.notes,
  };
}

function positionFromWire(scored: WirePosition): PositionScore {
  return {
    position: scored.position,
    beat: scored.beat,
    combined: scored.score_combined,
    left: scored.score_left,
    right: scored.score_right,
    notes: scored.notes,
  };
}

export function segmentEventId(sessionId: string, seq: number): string {
  return `${sessionId}:${seq}`;
}

function wireTakeEnd(end: NonNullable<RecordedTake['end']>): WireTakeEnd {
  const result = end.result;
  return {
    end_ms: end.endMs,
    end_reason: end.reason,
    grade: result.views.combined,
    grade_left: result.views.left,
    grade_right: result.views.right,
    counts: wireCounts(result.counts),
    positions: result.positions.map(wirePosition),
    segment_count: end.segmentCount,
    event_count: end.eventCount,
  };
}

function wireTake(take: RecordedTake): WireTake {
  const stamp = take.end ? take.end.stamp : take.stampAtStart;
  return {
    id: take.takeId,
    take_index: take.takeIndex,
    material_id: take.materialId,
    start_ms: take.startMs,
    first_seq: take.firstSeq,
    setup: take.setup,
    cell: wireCell(stamp.cell),
    frontier: wireFrontier(stamp.frontier),
    preset: stamp.preset,
    input_mode: stamp.input.mode,
    input_latency_ms: stamp.input.latencyMs,
    tempo_fastest_bpm: stamp.tempo.fastestBpm,
    tempo_goal_bpm: stamp.tempo.goalBpm,
    off_the_record: stamp.offTheRecord,
    engine_version: stamp.engineVersion,
    end: take.end ? wireTakeEnd(take.end) : null,
  };
}

function stampFromWire(surface: Surface, take: WireTake): TakeStamp {
  return {
    cell: cellFromWire(surface, take.cell),
    frontier: frontierFromWire(take.frontier),
    preset: take.preset,
    input: { mode: take.input_mode, latencyMs: take.input_latency_ms },
    tempo: {
      slowestBpm: take.frontier.tempo_bpm,
      fastestBpm: take.tempo_fastest_bpm,
      goalBpm: take.tempo_goal_bpm,
    },
    engineVersion: take.engine_version,
    offTheRecord: take.off_the_record,
  };
}

function resultFromWire(end: WireTakeEnd): TakeResult {
  return {
    views: { combined: end.grade, left: end.grade_left, right: end.grade_right },
    counts: countsFromWire(end.counts),
    positions: end.positions.map(positionFromWire),
  };
}

function wireSegment(sessionId: string, segment: Segment): WireSegment {
  return {
    client_event_id: segmentEventId(sessionId, segment.seq),
    seq: segment.seq,
    take_id: segment.takeId,
    off_the_record: segment.offTheRecord,
    first_ms: segment.events[0].timeMs,
    last_ms: segment.events[segment.events.length - 1].timeMs,
    event_count: segment.events.length,
    events: segment.events,
  };
}

export function wireSessionLinks(
  surface: Surface,
  surfaceSessionId: string | null,
): WireSessionLinks {
  return {
    play_session_id: surface === 'sight-reading' ? surfaceSessionId : null,
    rep_play_session_id: surface === 'repertoire' ? surfaceSessionId : null,
    exercise_play_session_id: surface === 'exercise' ? surfaceSessionId : null,
  };
}

/** The body for a session record (only sealed segments travel). */
export function toWire(record: SessionRecord): WireBatch {
  const header = record.header;
  return {
    session: {
      id: header.sessionId,
      surface: header.surface,
      ...wireSessionLinks(header.surface, header.surfaceSessionId),
      started_at: header.startedAt,
      format_version: header.formatVersion,
      app_version: header.appVersion,
      off_the_record: header.offTheRecord,
    },
    materials: record.materials.map((material) => ({
      id: material.materialId,
      phrase_id: material.phraseId,
      at_ms: material.atMs,
      score: material.score,
    })),
    takes: record.takes.map(wireTake),
    segments: record.segments.map((segment) => wireSegment(header.sessionId, segment)),
  };
}

/**
 * The session record back from a body. An ended take keeps only its final stamp (the stamp it
 * began with is not stored once it has ended).
 */
export function fromWire(batch: WireBatch): SessionRecord {
  const session = batch.session;
  return {
    header: {
      sessionId: session.id,
      surface: session.surface,
      surfaceSessionId:
        session.play_session_id ??
        session.rep_play_session_id ??
        session.exercise_play_session_id ??
        null,
      startedAt: session.started_at,
      formatVersion: session.format_version,
      appVersion: session.app_version,
      offTheRecord: session.off_the_record,
    },
    materials: batch.materials.map((material) => ({
      materialId: material.id,
      phraseId: material.phrase_id,
      atMs: material.at_ms,
      score: material.score,
    })),
    takes: [...batch.takes]
      .sort((a, b) => a.take_index - b.take_index)
      .map((take): RecordedTake => {
        const stamp = stampFromWire(session.surface, take);
        return {
          takeId: take.id,
          takeIndex: take.take_index,
          startMs: take.start_ms,
          firstSeq: take.first_seq,
          materialId: take.material_id,
          setup: take.setup,
          stampAtStart: stamp,
          end: take.end
            ? {
                endMs: take.end.end_ms,
                reason: take.end.end_reason,
                stamp,
                result: resultFromWire(take.end),
                segmentCount: take.end.segment_count,
                eventCount: take.end.event_count,
              }
            : null,
        };
      }),
    segments: [...batch.segments]
      .sort((a, b) => a.seq - b.seq)
      .map((segment) => ({
        seq: segment.seq,
        takeId: segment.take_id,
        offTheRecord: segment.off_the_record,
        events: segment.events,
      })),
  };
}
